/**
 * Integration data configuration
 * 
 * Centralized source of truth for all platform integrations.
 * Used by both the landing page component and integrations page.
 * 
 * Reference: Integration management best practices
 * https://docs.sentry.io/product/integrations/
 */

export type IntegrationStatus = 'coming-soon' | 'available' | 'beta';
export type IntegrationCategory = 'meeting' | 'productivity' | 'crm' | 'storage';

export interface Integration {
  /**
   * Display name of the integration
   */
  name: string;
  
  /**
   * Icon identifier or emoji
   */
  icon: string;
  
  /**
   * Category for grouping integrations
   */
  category: IntegrationCategory;
  
  /**
   * Short description of what the integration does
   */
  description?: string;
  
  /**
   * Whether this integration is featured/promoted
   */
  featured?: boolean;
  
  /**
   * Availability status
   * - 'available': Fully functional and ready to use
   * - 'beta': Available but may have limitations
   * - 'coming-soon': Not yet available
   */
  status: IntegrationStatus;
  
  /**
   * Optional link to integration documentation or setup page
   */
  link?: string;
}

/**
 * All available integrations
 */
export const integrations: Integration[] = [
  // Meeting Platforms
  {
    name: 'Zoom',
    icon: '🔵',
    category: 'meeting',
    description: 'Record and transcribe Zoom meetings automatically',
    featured: true,
    status: 'available',
  },
  {
    name: 'Google Meet',
    icon: '🟢',
    category: 'meeting',
    description: 'Seamless integration with Google Meet',
    featured: true,
    status: 'available',
  },
  {
    name: 'Microsoft Teams',
    icon: '🔷',
    category: 'meeting',
    description: 'Native Teams integration',
    featured: true,
    status: 'available',
  },
  {
    name: 'Webex',
    icon: '💻',
    category: 'meeting',
    description: 'Cisco Webex support',
    featured: false,
    status: 'beta',
  },
  
  // Productivity
  {
    name: 'Slack',
    icon: '💬',
    category: 'productivity',
    description: 'Share meeting summaries in Slack channels',
    featured: true,
    status: 'available',
  },
  {
    name: 'Notion',
    icon: '📝',
    category: 'productivity',
    description: 'Export notes and summaries to Notion',
    featured: false,
    status: 'available',
  },
  {
    name: 'Google Drive',
    icon: '📁',
    category: 'productivity',
    description: 'Store recordings in Google Drive',
    featured: false,
    status: 'available',
  },
  {
    name: 'OneDrive',
    icon: '☁️',
    category: 'productivity',
    description: 'Microsoft OneDrive integration',
    featured: false,
    status: 'available',
  },
  
  // CRM & Sales
  {
    name: 'Salesforce',
    icon: '⚡',
    category: 'crm',
    description: 'Sync meeting data with Salesforce',
    featured: true,
    status: 'beta',
  },
  {
    name: 'HubSpot',
    icon: '🎯',
    category: 'crm',
    description: 'HubSpot CRM integration',
    featured: false,
    status: 'coming-soon',
  },
  {
    name: 'Pipedrive',
    icon: '📊',
    category: 'crm',
    description: 'Pipedrive CRM support',
    featured: false,
    status: 'coming-soon',
  },
  {
    name: 'Zoho',
    icon: '🔷',
    category: 'crm',
    description: 'Zoho CRM integration',
    featured: false,
    status: 'coming-soon',
  },
  
  // Storage
  {
    name: 'Dropbox',
    icon: '📦',
    category: 'storage',
    description: 'Store and sync recordings with Dropbox',
    featured: false,
    status: 'coming-soon',
  },
];

/**
 * Category definitions with labels
 */
export const categories: Array<{
  id: IntegrationCategory;
  label: string;
}> = [
  {id: 'meeting', label: 'Meeting Platforms'},
  {id: 'productivity', label: 'Productivity'},
  {id: 'crm', label: 'CRM & Sales'},
  {id: 'storage', label: 'Storage'},
];

/**
 * Get integrations by category
 */
export function getIntegrationsByCategory(category: IntegrationCategory): Integration[] {
  return integrations.filter((integration) => integration.category === category);
}

/**
 * Get featured integrations
 */
export function getFeaturedIntegrations(): Integration[] {
  return integrations.filter((integration) => integration.featured === true);
}

/**
 * Get integrations by status
 */
export function getIntegrationsByStatus(status: IntegrationStatus): Integration[] {
  return integrations.filter((integration) => integration.status === status);
}

/**
 * Get available integrations (status === 'available')
 */
export function getAvailableIntegrations(): Integration[] {
  return getIntegrationsByStatus('available');
}

